<template>
  <div
    class="condition-row"
    :class="{
      'condition-row--readonly': !canManageRules,
      'condition-row--invalid': hasValidationError,
      'is-dragging': isDragging,
      'is-drop-target': isDropTarget
    }"
    @dragenter.prevent="$emit('drag-over', condition.id)"
    @dragover.prevent="$emit('drag-over', condition.id)"
    @drop="$emit('drop', condition.id)"
  >
    <img
      v-if="canManageRules"
      src="~@/assets/images/icons/drag-handle.svg"
      alt="drag handle"
      class="drag-handle"
      draggable="true"
      @click.stop
      @dragstart="handleDragStart"
      @dragend="handleDragEnd"
    />
    <div v-if="isLoading(0)" class="condition-skeleton"></div>
    <custom-dropdown
      v-else
      :list="filteredSourceItems"
      :selected="condition.fieldType"
      :default-style="fieldTypeStyle"
      prevent-deselection
      @select="selectFieldType"
    />
    <div v-if="isLoading(1)" class="condition-skeleton"></div>
    <component
      v-else-if="isDynamicFieldSource"
      :is="fieldComponent"
      :value="dynamicFieldValue"
      :default-style="fieldStyle"
      :name="`condition-field-${condition.id}`"
      @input="selectDynamicField"
    />
    <custom-dropdown
      v-else
      :list="conditionFieldItems"
      :selected="selectedConditionField"
      :default-style="fieldStyle"
      prevent-deselection
      @select="selectField"
    >
      <template #label="{ item, defaultLabel }">
        <div class="elipsis-text mr-4" v-overflow-tooltip>
          {{ displayValue(item) || defaultLabel }}
        </div>
      </template>
      <template #item="{ item }">
        <div class="elipsis-text list-name" v-overflow-tooltip>{{ displayValue(item) }}</div>
      </template>
    </custom-dropdown>
    <div v-if="isLoading(2)" class="condition-skeleton"></div>
    <custom-dropdown
      v-else
      :key="`operator-${condition.id}-${selectedInputType}`"
      :list="operatorItems"
      :selected="selectedOperator"
      :default-style="operatorStyle"
      prevent-deselection
      @select="selectOperator"
    />
    <div v-if="isLoading(3)" class="condition-skeleton"></div>
    <component
      v-else
      :is="valueComponent"
      :key="conditionInputKey"
      :value="condition.value"
      :list="valueItems"
      :default-style="valueStyle"
      :name="`condition-value-${condition.id}`"
      @input="selectValue"
    />
    <button v-if="canManageRules" class="icon-btn delete-action" type="button" @click="$emit('remove', condition.id)">
      <delete-icon width="14px" height="14px" />
    </button>
    <p v-if="hasNegativeValueError" class="condition-row__error">
      Negative values are not allowed for Number fields.
    </p>
    <p v-else-if="addAttemptError" class="condition-row__error">
      Complete the field, operator, and value before adding another condition.
    </p>
  </div>
</template>

<script>
import CustomDropdown from '@/components/common/customDropdown.vue'
import DeleteIcon from '@/components/common/icons/deleteIcon.vue'
import { clone } from './configModel'
import { fetchSourceItems, findOperator, findOptionByValue, getAvailableConditionFields, getCrossGroupRestrictedFieldIds, getCustomListLookupKey, getDefaultConditionSelection, getDefaultOperatorForField, getMaterialCondtions, isIncludesOperator, isNegativeValue, normalizeInputType, normalizeStringListValue, optionDisplayLabel, optionValue, shouldRestrictNegativeConditionValue, withValidationStyle } from './conditionHelper'
import { CONFIG_DYNAMIC_ELEMENTS } from './configDynamicElements'
import { INPUT_TYPES } from './configConstants'
import { mapGetters } from 'vuex/dist/vuex.common.js'
import config from '@/config'

export default {
  name: 'ConditionRow',
  components: {
    CustomDropdown,
    DeleteIcon
  },
  props: {
    condition: {
      type: Object,
      required: true
    },
    groupConditions: {
      type: Array,
      default: () => []
    },
    conditionGroups: {
      type: Array,
      default: () => []
    },
    groupMatch: {
      type: String,
      default: 'AND'
    },
    currentGroupIndex: {
      type: Number,
      required: true
    },
    lists: {
      type: Object,
      required: true
    },
    selectedTab: {
      type: Object,
      required: true
    },
    selectedConfig: {
      type: [String, Object],
      required: true
    },
    validation: {
      type: Object,
      default: () => ({})
    },
    addAttemptError: {
      type: Boolean,
      default: false
    },
    canManageRules: {
      type: Boolean,
      default: false
    },
    isDragging: {
      type: Boolean,
      default: false
    },
    isDropTarget: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loadingFrom: 1,
      loadVersion: 0,
      sourceItems: [],
      // conditionFieldItems: [],
      selectedFieldItem: null,
      dropdownStyles: {
        fieldType: {
          dropDownWidth: '190px',
          borderColor: '#dfe5ed',
          backGroundColor: '#f4f5f7',
          labelHeight: '30px',
          ddLabelFontSize: 'var(--text-xs2)'
        },
        field: {
          dropDownWidth: '144px',
          borderColor: '#dfe5ed',
          backGroundColor: '#f4f5f7',
          labelHeight: '30px',
          ddLabelFontSize: 'var(--text-xs2)'
        },
        operator: {
          dropDownWidth: '112px',
          borderColor: '#dfe5ed',
          backGroundColor: '#f4f5f7',
          labelHeight: '30px',
          ddLabelFontSize: 'var(--text-xs2)'
        },
        value: {
          dropDownWidth: '100%',
          borderColor: '#dfe5ed',
          backGroundColor: '#ffffff',
          labelHeight: '30px',
          ddLabelFontSize: 'var(--text-xs2)'
        }
      }
    }
  },
  computed: {
    ...mapGetters('tenantConfig', ['getCustomListMap']),
    filteredSourceItems () {
      return fetchSourceItems({
        selectedTab: this.selectedTab.name,
        selectedConfig: this.selectedConfig,
        sourceFieldTypes: this.lists.sourceFieldTypes
      })
    },
    fieldTypeStyle () {
      return withValidationStyle(this.dropdownStyles.fieldType, this.validation.fieldType)
    },
    fieldStyle () {
      return withValidationStyle(this.dropdownStyles.field, this.validation.field)
    },
    isDynamicFieldSource  () {
      return optionValue(this.condition.fieldType) === 'PLUGIN_CUSTOM_FIELDS'
    },
    dynamicFieldValue () {
      return typeof this.condition.field === 'object'
        ? optionValue(this.condition.field) || ''
        : this.condition.field
    },
    selectedConditionField () {
      if (this.isDynamicFieldSource) {
        return {
          id: this.dynamicFieldValue,
          name: this.dynamicFieldValue,
          value: this.dynamicFieldValue,
          type: INPUT_TYPES.TEXT
        }
      }
      const selectedField = optionValue(this.condition.field)
      return this.selectedFieldItem || this.conditionFieldItems.find(item => {
        return optionValue(item) === selectedField
      }) || (this.condition.field && typeof this.condition.field === 'object' ? this.condition.field : {})
    },
    selectedInputType () {
      return normalizeInputType(this.selectedConditionField.type) || INPUT_TYPES.TEXT
    },
    conditionCustomListKey () {
      if (this.selectedInputType !== INPUT_TYPES.CUSTOM_LIST) {
        return null
      }

      return getCustomListLookupKey(this.selectedConditionField)
    },
    usesTagInput () {
      return isIncludesOperator(this.condition.operator) && this.selectedInputType !== INPUT_TYPES.CUSTOM_LIST
    },
    usesMultiSelectList () {
      return isIncludesOperator(this.condition.operator) && this.selectedInputType === INPUT_TYPES.CUSTOM_LIST
    },
    valueComponent () {
      if (this.usesMultiSelectList) {
        return CONFIG_DYNAMIC_ELEMENTS.multi_list
      }
      if (this.usesTagInput) {
        return CONFIG_DYNAMIC_ELEMENTS.tags
      }
      return CONFIG_DYNAMIC_ELEMENTS[this.selectedInputType] || CONFIG_DYNAMIC_ELEMENTS.text
    },
    fieldComponent () {
      return CONFIG_DYNAMIC_ELEMENTS.text
    },
    conditionFieldItems () {
      if (this.selectedTab.name === 'MATERIAL') {
        const selectedField = optionValue(this.condition.field)
        const allowDuplicates = (this.groupMatch || 'AND') === 'OR'
        const availableFields = getAvailableConditionFields({
          selectedTab: this.selectedTab.name,
          selectedSource: this.condition.fieldType,
          groupConditions: this.groupConditions,
          conditionId: this.condition.id,
          allowDuplicates
        })
        const crossGroupRestrictedFieldIds = getCrossGroupRestrictedFieldIds({
          conditionGroups: this.conditionGroups,
          groupIndex: this.currentGroupIndex,
          selectedSource: this.condition.fieldType,
          currentConditionId: this.condition.id
        })
        const sourceFields = getMaterialCondtions({ selectedSource: this.condition.fieldType })
        const filteredSourceFields = optionValue(this.condition.fieldType) === 'STANDARD_MATERIAL_FORM'
          ? sourceFields.filter(item => item?.type !== config.FORM_TYPE.TABLE && item?.type !== config.FORM_TYPE.DATE && item?.type !== config.FORM_TYPE.TIME)
          : sourceFields
        return filteredSourceFields.filter((item) => {
          return (optionValue(item) === selectedField || availableFields.some(field => optionValue(field) === optionValue(item))) &&
            (optionValue(item) === selectedField || !crossGroupRestrictedFieldIds.has(optionValue(item)))
        })
      }
      return []
    },
    operatorItems () {
      return findOperator(this.selectedInputType)
    },
    operatorStyle () {
      return withValidationStyle(this.dropdownStyles.operator, this.validation.operator)
    },
    selectedOperator () {
      return this.condition.operator
    },
    hasNegativeValueError () {
      return shouldRestrictNegativeConditionValue(this.condition) && (Boolean(this.validation?.negativeValue) || isNegativeValue(this.condition.value))
    },
    valueStyle () {
      return withValidationStyle(this.dropdownStyles.value, this.validation.value || this.hasNegativeValueError)
    },
    valueItems () {
      if (this.usesTagInput) {
        return []
      }
      if (this.selectedInputType === INPUT_TYPES.CUSTOM_LIST) {
        return this.getCustomListMap[this.conditionCustomListKey] || []
      }
      return []
    },
    conditionInputKey () {
      return [
        this.condition.id,
        optionValue(this.condition.fieldType) || 'no-source',
        optionValue(this.condition.field) || 'no-field',
        optionValue(this.condition.operator) || 'no-operator',
        this.selectedInputType
      ].join('-')
    },
    hasValidationError () {
      return Object.values(this.validation || {}).some(Boolean) || this.hasNegativeValueError
    }
  },
  watch: {
    filteredSourceItems: {
      handler (nextSources) {
        const selectedFieldType = optionValue(this.condition.fieldType)
        const isCurrentSourceValid = nextSources.some(item => optionValue(item) === selectedFieldType)
        if (isCurrentSourceValid) {
          return
        }
        const defaults = getDefaultConditionSelection({
          selectedTab: this.selectedTab.name,
          selectedConfig: this.selectedConfig,
          sourceFieldTypes: this.lists.sourceFieldTypes,
          groupConditions: this.groupConditions.filter(item => item.id !== this.condition.id),
          allowDuplicates: (this.groupMatch || 'AND') === 'OR'
        })
        this.updateSelectedConditionPatch({
          fieldType: defaults.fieldType,
          field: defaults.field,
          operator: getDefaultOperatorForField(defaults.field),
          value: this.getDefaultValueForField(defaults.field)
        })
      },
      immediate: true
    }
  },
  created () {
  },
  methods: {
    getDefaultValueForField (field) {
      return field?.type === INPUT_TYPES.BOOLEAN ? true : ''
    },
    toOption (value) {
      if (value === undefined || value === null || value === '') return {}
      if (typeof value === 'object') return value
      return { id: value, name: value }
    },
    fieldTypeOption (value) {
      return findOptionByValue(this.filteredSourceItems, value) || this.toOption(value)
    },
    isLoading (position) {
      // return this.loadingFrom !== null && position > this.loadingFrom
      return false
    },
    async resolveOptions (getOptions) {
      const options = await Promise.resolve(getOptions())
      return Array.isArray(options) ? options : []
    },
    selectFieldType (item) {
      this.selectedFieldItem = null
      const nextField = getAvailableConditionFields({
        selectedTab: this.selectedTab.name,
        selectedSource: item,
        groupConditions: this.groupConditions,
        conditionId: this.condition.id,
        allowDuplicates: (this.groupMatch || 'AND') === 'OR'
      })[0]
      this.updateSelectedConditionPatch({
        fieldType: item,
        field: nextField || {},
        operator: getDefaultOperatorForField(nextField),
        value: this.getDefaultValueForField(nextField)
      })
    },
    selectField (item) {
      this.selectedFieldItem = item
      this.updateSelectedConditionPatch({
        field: item,
        operator: getDefaultOperatorForField(item),
        value: this.getDefaultValueForField(item)
      })
    },
    selectDynamicField (value) {
      this.selectedFieldItem = null
      this.updateSelectedConditionPatch({
        field: value,
        operator: getDefaultOperatorForField({ type: INPUT_TYPES.TEXT }),
        value: ''
      })
    },
    selectOperator (item) {
      const nextPatch = {
        operator: item
      }

      if (isIncludesOperator(item) && this.selectedInputType === INPUT_TYPES.CUSTOM_LIST) {
        nextPatch.value = this.normalizeMultiSelectValue(this.condition.value)
      } else if (isIncludesOperator(item)) {
        nextPatch.value = normalizeStringListValue(this.condition.value)
      } else if (this.usesMultiSelectList) {
        nextPatch.value = Array.isArray(this.condition.value) ? (this.condition.value[0] || '') : this.condition.value
      } else if (this.usesTagInput) {
        nextPatch.value = ''
      }

      this.updateSelectedConditionPatch(nextPatch)
    },
    selectValue (value) {
      this.updateSelectedCondition(3, 'value', this.usesTagInput ? normalizeStringListValue(value) : value)
    },
    updateSelectedCondition (position, key, value) {
      this.updateSelectedConditionPatch({ [key]: value })
    },
    updateSelectedConditionPatch (patch) {
      const next = clone(this.condition)
      Object.assign(next, patch)
      this.$emit('update', next)
    },
    displayValue (value) {
      return optionDisplayLabel(value)
    },
    normalizeMultiSelectValue (value) {
      if (!Array.isArray(value)) {
        return value ? [value] : []
      }
      return value.filter(item => item !== undefined && item !== null && item !== '')
    },
    handleDragStart (event) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', this.condition.id)
      if (event.dataTransfer.setDragImage) {
        event.dataTransfer.setDragImage(this.$el, 24, 15)
      }
      this.$emit('drag-start', this.condition.id)
    },
    handleDragEnd () {
      this.$emit('drag-end')
    }
  }
}
</script>

<style lang="scss" scoped>
.condition-row {
  display: grid;
  align-items: center;
  grid-template-columns: 20px 190px 144px 112px minmax(180px, 1fr) 24px;
  gap: 8px;
  margin-bottom: 10px;
  border-radius: 4px;
  transition: background 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease, transform 0.15s ease;

  &:hover .drag-handle {
    opacity: 0.58;
  }
}

.condition-row.condition-row--invalid {
  padding-bottom: 28px;
  position: relative;
}

.condition-row.condition-row--readonly {
  grid-template-columns: 190px 144px 112px minmax(180px, 1fr);
}

.condition-row.is-dragging {
  opacity: 0.45;
  transform: scale(0.995);
  background: #f8fafc;
}

.condition-row.is-drop-target:not(.is-dragging) {
  background: #f5f9ff;
  box-shadow: inset 0 2px 0 #8bb8ff;
}

.drag-handle {
  width: 14px;
  height: 14px;
  cursor: grab;
  opacity: 0.18;
  transition: opacity 0.15s ease;

  &:active {
    cursor: grabbing;
    opacity: 0.9;
  }
}

.condition-skeleton {
  position: relative;
  height: 34px;
  overflow: hidden;
  border: 1px solid #e4e9f0;
  border-radius: 4px;
  background: #eef1f5;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.75), transparent);
    transform: translateX(-100%);
    animation: condition-skeleton-shimmer 1.2s ease-in-out infinite;
  }
}

@keyframes condition-skeleton-shimmer {
  to {
    transform: translateX(100%);
  }
}

.icon-btn {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #98a2b3;
  cursor: pointer;
}

.delete-action:hover {
  background: #fff1f0;
}

.delete-action:hover ::v-deep .delete-icon {
  color: #f04438;
}

.condition-row__error {
  position: absolute;
  left: 28px;
  bottom: 6px;
  margin: 0;
  color: #dc2626;
  font-size: var(--text-xs2);
  font-weight: 500;
}

::v-deep .custom-dropdown-container,
::v-deep .custom-select {
  width: 100%;
  max-width: 100%;
}

@media (max-width: 900px) {
  .condition-row {
    grid-template-columns: 1fr;
  }

  .condition-row__error {
    position: static;
  }

  .drag-handle {
    display: none;
  }
}
</style>
